//#region Util Imports

import * as _ from 'underscore'
import * as moment from 'moment'

//#endregion

//#region DevExtreme Imports

import DevExpress from "devextreme";
import CustomStore from 'devextreme/data/custom_store';
import DataSource from 'devextreme/data/data_source';
import { dxButtonOptions } from "devextreme/ui/button";
import { LoadOptions } from 'devextreme/data/load_options';

//#endregion

import { GridService } from "../../../services/grid";
import { Dictionary, LocalizationHelper, ReferenceTypeComponent, TypeHelper } from "../../../helpers";
import { UserSessionService } from "../../../services/user-session";
import { IMRBaseControlController } from "../base";
import { GridController, GridOptions, GridTypes } from "../grid";
import { TreeListOptions } from "../tree-list";
import { DropDownBox, DropDownBoxOptions } from "./drop-down-box.interfaces";
import { ColumnModel } from "../../../modules/api/api.models";
import { Enums } from "../../../models/common";
import { ColumnInfo } from "../../../models/custom";
import { ReferenceTypeService } from "../../../services/reference-type";
import { DataProviderListResult } from '../../../data-provider';
import { IReferenceType } from '../../../models/util/interfaces';
import { DistributorHierarchyService } from '../../../services/distributor';
import { TreeListController } from '../tree-list/tree-list.controller';
import { DataTypeGroupService } from '../../../services/data-type-group';
import { dxValidatorOptions } from 'devextreme/ui/validator';

export class DropDownBoxController
    extends
    IMRBaseControlController<DropDownBox, DropDownBoxOptions>
    implements
    DropDownBox {
    public dropDownBoxOptions: DevExpress.ui.dxDropDownBoxOptions
    public gridOptions: GridOptions
    public toolbarOptions: DevExpress.ui.dxToolbarOptions
    public treeListOptions: TreeListOptions

    public showNoDataMessage: boolean
    public isOpened: boolean

    private selectBoxValue: number[]
    private currentReferenceObject: IReferenceType[]
    private currentReferenceType: Enums.ReferenceType
    private prevReferenceType: Enums.ReferenceType
    private referenceHasView: boolean

    private dropDownBox: DevExpress.ui.dxDropDownBox
    private grid: GridController<IReferenceType>
    private treeList: TreeListController<IReferenceType>

    public get noDataText(): string { return LocalizationHelper.getLocalizedText(LocalizationHelper.Text.NoData) }
    private get maxPopupWidth(): number { return 700 }

    private multipleSelectionEnabled: boolean

    private validationOptions: dxValidatorOptions

    // Ctor

    constructor(
        private readonly userSession: UserSessionService,
        private readonly gridService: GridService,
        private readonly referenceTypeService: ReferenceTypeService,
        private readonly distributorHierarchyService: DistributorHierarchyService,
        private readonly dataTypeGroupService: DataTypeGroupService,
    ) {
        super()
    }

    // Ctor End

    public changeReferenceType(idReferenceType: Enums.ReferenceType): void {
        this.selectBoxValue = null
        this.setReferenceType(idReferenceType)
        this.initializeDropDownBoxContent()
    }

    public readOnly(value: boolean) {
        this.dropDownBox.option({ readOnly: value })
    }

    //#region Component    

    protected initialize(options: DropDownBoxOptions): JQueryPromise<void> {
        this.setReferenceType(options.referenceType)
        this.selectBoxValue = this.options.value
        this.validationOptions = { validationRules: options.validationRulles }

        return $.when(
            this.initializeDropDownBox(),
            this.initializeDropDownBoxContent(),
            this.initializeToolbar())
    }

    //#endregion

    private setReferenceType(idReferenceType: Enums.ReferenceType): void {
        this.prevReferenceType = this.currentReferenceType
        this.currentReferenceType = this.getReferenceType(idReferenceType)

        this.multipleSelectionEnabled = this.options.multiSelectEnabled || _.contains([Enums.ReferenceType.OperatorList, Enums.ReferenceType.TaskStatusList, Enums.ReferenceType.LocationList, Enums.ReferenceType.DistributorList, Enums.ReferenceType.MeterTypeList, Enums.ReferenceType.DeviceTypeList, Enums.ReferenceType.AreaList], idReferenceType)

        if (this.prevReferenceType != this.currentReferenceType) {
            this.clear()
        }
    }

    private clear(): void {
        this.grid = null
        this.gridOptions = null

        this.treeList = null
        this.treeListOptions = null
    }

    private initializeDropDownBox(): JQueryPromise<any> {
        this.dropDownBoxOptions = {
            bindingOptions: {
                value: '$ctrl.selectBoxValue',
            },
            valueExpr: (item: IReferenceType) => {
                if (item != null) {
                    return item.getReferenceKey()
                }
            },
            displayExpr: (item: IReferenceType) => {
                if (item != null) {
                    return item.toString()
                }

                return LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Loading)
            },
            placeholder: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Select),
            showClearButton: true,
            dataSource: [],
            dropDownOptions: {
                width: this.maxPopupWidth,
            },
            onValueChanged: (e: any) => {
                if (_.isFunction(this.options.onValueChanged)) {
                    this.options.onValueChanged(e.value, this.currentReferenceObject)
                }

                this.dropDownBox.close()
            },
            onOpened: (e: any) => {
                setTimeout(() => {
                    this.selectRows()
                }, 500)
            },
            onClosed: (e: any) => {
                this.isOpened = false
            },
            onInitialized: (e: any) => {
                this.dropDownBox = e.component
            }
        }

        return super.waitForOnInitialized(this.dropDownBoxOptions)
    }

    private initializeDropDownBoxContent(): void {
        this.clear()

        this.showNoDataMessage = this.currentReferenceType == null || this.currentReferenceType == Enums.ReferenceType.None

        if (this.showNoDataMessage == false) {
            this.getHasViewDictionary([this.currentReferenceType])
                .then((hasView: Dictionary<boolean>) => {
                    this.referenceHasView = !!hasView.tryGetValue(this.currentReferenceType)

                    if (this.referenceHasView == true || this.currentReferenceType === Enums.ReferenceType.IdDataType) {
                        $.when<any>(
                            this.gridService.getPopupColumnModelByReferenceType(this.currentReferenceType),
                            this.distributorHierarchyService.isDistributorHierarchyEnabled()
                        )
                            .then((columns: ColumnModel[], isDistributorHierarchyEnabled: boolean) => {
                                if (this.selectBoxValue != null) {
                                    this.getReferenceObjectForSelectedValue(this.selectBoxValue)
                                }

                                if ((this.currentReferenceType === Enums.ReferenceType.IdDistributor && isDistributorHierarchyEnabled && this.options.ignoreHierarhcy !== true)
                                    || this.currentReferenceType === Enums.ReferenceType.IdDataTypeGroup) {
                                    this.initializeTreeList(columns)
                                } else {
                                    this.initializeGrid(columns)
                                }
                            })
                    } else {
                        const column: ColumnModel = new ColumnModel()
                        column.caption = LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Name)
                        column.idName = 'name'
                        column.name = 'name'

                        this.initializeGrid([column])
                    }
                })
        }
    }

    private initializeGrid(columns: ColumnModel[]): void {
        if (this.prevReferenceType !== this.currentReferenceType) {
            this.gridOptions = null

            setTimeout(() => {
                this.initializeGripOptions(columns)
            })
        } else {
            this.initializeGripOptions(columns)
        }
    }

    private initializeGripOptions(columns: ColumnModel[]): void {
        const moduleName: Enums.SimaxModuleName = ReferenceTypeComponent.getModuleByReferenceType(this.currentReferenceType)

        this.gridOptions = {
            name: '',//this.getModuleName(moduleName) + '_Select_Popover',
            type: GridTypes.PAGINATED_SMALL,
            columnAutoWidth: true,
            columnsDictionary: columns,
            idReferenceType: this.currentReferenceType,
            selection: {
                allowSelectAll: false,
                mode: this.multipleSelectionEnabled ? 'multiple' : 'single',
                showCheckBoxes: this.multipleSelectionEnabled ? 'always' : 'none'
            },
            buttonsOptions: {
                enabled: true,
                isSelectButtonHidden: true,
                dashboardSettings: [{ visible: true, option: Enums.GridOption.ColumnChooser }]
            },
            getDataSourceMethod: (columns: ColumnInfo[], pageNumber: number, pageSize: number, customWhereClause: string): any => {
                return this.getGridDataSource(columns, pageNumber, pageSize, customWhereClause)
            },
            onInitialized: (e: any, grid: GridController<IReferenceType>) => {
                this.grid = grid

                setTimeout(() => {
                    this.selectRows()
                }, 500)
            },
            onRowDoubleClick: (e: any) => {
                if (this.multipleSelectionEnabled !== true) {
                    this.onRowSelected()
                }
            }
        }

        if (this.referenceHasView !== true) {
            this.gridOptions.getDataSource = () => this.getNonViewDataSource()
            this.gridOptions.onCellPrepared = (e: any) => {
                if (e.rowType === 'data' && e.column.dataField === 'name') {
                    const data: IReferenceType = this.gridService.getSourceObjectFromGridObject(e.data, this.currentReferenceType)

                    if (data != null) {
                        e.cellElement.html(data.toString())
                    }
                }
            }
        }
    }

    private initializeTreeList(columns: ColumnModel[]): void {
        if (this.currentReferenceType !== this.prevReferenceType) {
            this.treeListOptions = null

            setTimeout(() => {
                this.initializeTreeListOptions(columns)
            })
        } else {
            this.initializeTreeListOptions(columns)
        }
    }

    private initializeTreeListOptions(columns: ColumnModel[]): void {
        this.treeListOptions = {
            addCustomFilters: true,
            columnsDictionary: columns,
            dataSource: new CustomStore({
                load: (opt: LoadOptions) => {
                    return this.getTreeListDataSource(this.currentReferenceType, columns)
                }
            }),
            keyExpr: this.getTreeListIdNodeFieldName(),
            parentIdExpr: this.getTreeListIdNodeParentFieldName(),
            name: '', // 'dataTypeGroupsTreeList_Select_Popover_' + this.currentReferenceType,
            referenceId: this.currentReferenceType,
            idReferenceType: this.currentReferenceType,
            type: GridTypes.NO_SEARCH_BAR_PAGINATED_SMALL,
            selection: {
                allowSelectAll: true,
                mode: this.multipleSelectionEnabled ? 'multiple' : 'single',
            },
            buttonsOptions: {
                enabled: true,
                isSelectButtonHidden: this.multipleSelectionEnabled
            },
            onInitialized: (e: any, grid: TreeListController<IReferenceType>) => {
                this.treeList = grid
                this.treeList.dxTree.option('selection', {
                    allowSelectAll: true,
                    mode: this.multipleSelectionEnabled ? 'multiple' : 'single',
                })

                this.treeList.dxTree.option('stateStoring.enable', false)

                setTimeout(() => {
                    this.selectRows()
                }, 500)
            },
            onRowDoubleClick: (e: any) => {
                this.onRowSelected()
            },
        }
    }

    private initializeToolbar() {
        this.toolbarOptions = {
            items: [{
                location: 'after',
                widget: 'dxButton',
                options: <dxButtonOptions>{
                    text: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Ok),
                    onClick: (e: any) => {
                        this.onRowSelected()
                    }
                }
            }, {
                location: 'after',
                widget: 'dxButton',
                options: <dxButtonOptions>{
                    text: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Cancel),
                    onClick: (e: any) => {
                        this.dropDownBox.close()
                    }
                }
            }]
        }
    }

    private getModuleName(moduleId: Enums.SimaxModuleName): string {
        let moduleName: string

        TypeHelper.iterateEnum(Enums.SimaxModuleName, (key: Enums.SimaxModuleName, name: string) => {
            if (key == moduleId) {
                moduleName = name
            }
        })
        return moduleName
    }

    private onRowSelected() {
        if (this.grid != null) {
            this.selectBoxValue = this.grid.selectedItemsIds
            this.currentReferenceObject = this.grid.selectedItems
        } else if (this.treeList != null) {
            this.selectBoxValue = this.treeList.selectedItemsIds
            this.currentReferenceObject = this.treeList.selectedItems
        }

        this.dropDownBox.close()
    }

    private selectRows(): void {
        if (this.grid != null) {
            this.grid.dxGrid.selectRows(this.selectBoxValue, false)
        } else if (this.treeList != null) {
            this.treeList.dxTree.selectRows(this.selectBoxValue, false)
        }
    }

    //#region Utils

    private getHasViewDictionary(referenceTypeIds: ReadonlyArray<Enums.ReferenceType>): JQueryPromise<Dictionary<boolean>> {
        return this.referenceTypeService.isViewReferenceTypes(referenceTypeIds)
    }

    protected getReferenceTable(referenceType: Enums.ReferenceType): JQueryPromise<IReferenceType[]> {
        return this.referenceTypeService
            .getReferenceObjects(referenceType)
            .then((result: DataProviderListResult<IReferenceType>) => {
                return _.map(result.objects, (referenceObject: IReferenceType) => {
                    return referenceObject
                })
            })
    }

    private getGridDataSource(columns: ColumnInfo[], pageNumber: number, pageSize: number, customWhereClause: string, referenceValues?: number[]): JQueryPromise<DataProviderListResult<IReferenceType>> {
        return this.referenceTypeService.getReferenceObjects(this.currentReferenceType, {
            filterOptions: {
                columns,
                customWhereClause
            },
            pagingOptions: {
                pageNumber,
                pageSize
            },
            referenceValues: referenceValues
        })
            .then((result: DataProviderListResult<IReferenceType>) => {
                this.setDropDownBoxDataSource(result.objects)
                return result
            })
    }

    private getNonViewDataSource(): JQueryPromise<IReferenceType[]> {
        return this.userSession
            .getDataProvider()
            .System
            .getReferenceObjectList(this.currentReferenceType, [], [], 0, 0, '', 0)
            .then((result: DataProviderListResult<IReferenceType>) => {
                this.setDropDownBoxDataSource(result.objects)
                return result.objects
            })
    }

    private getTreeListDataSource(idReferenceType: Enums.ReferenceType, columns: ColumnModel[]): JQueryPromise<IReferenceType[]> {
        switch (idReferenceType) {
            case Enums.ReferenceType.IdDataTypeGroup:
                return this.dataTypeGroupService
                    .getDataTypeGroups(columns)
                    .then((result: DataProviderListResult<any>) => {
                        this.setDropDownBoxDataSource(_.map(result.objects, (item: any) => this.gridService.getSourceObjectFromGridObject(item, this.currentReferenceType)))
                        return result.objects
                    })
            case Enums.ReferenceType.IdDistributor:
                return this.distributorHierarchyService.getTreeListDataSource(columns)
                    .then((objects: any[]) => {
                        this.setDropDownBoxDataSource(_.map(objects, (item: any) => this.gridService.getSourceObjectFromGridObject(item, this.currentReferenceType)))
                        return objects
                    })
        }
    }

    private setDropDownBoxDataSource(objects: IReferenceType[]): void {
        const dataSource: IReferenceType[] = _.map(objects, (item: IReferenceType) => {
            return item
        })

        if (this.currentReferenceObject != null) {
            dataSource.push(...this.currentReferenceObject)
        }

        this.dropDownBox.option({ dataSource: dataSource })
    }

    private getTreeListIdNodeFieldName(): string {
        switch (this.currentReferenceType) {
            case Enums.ReferenceType.IdDistributor:
                return 'idDistributorHierarchy' //from distributor.hierarchy.service
            case Enums.ReferenceType.IdDataTypeGroup:
                return DataTypeGroupService.ID_FIELD
            default:
                return undefined
        }
    }

    private getTreeListIdNodeParentFieldName(): string {
        switch (this.currentReferenceType) {
            case Enums.ReferenceType.IdDistributor:
                return 'idDistributorHierarchyParent' //from distributor.hierarchy.service
            case Enums.ReferenceType.IdDataTypeGroup:
                return DataTypeGroupService.PARENT_ID_FIELD
            default:
                return undefined
        }
    }

    private getReferenceObjectForSelectedValue(value: number[]) {
        this.getHasViewDictionary([this.currentReferenceType])
            .then((hasView: Dictionary<boolean>) => {
                if (hasView.tryGetValue(this.currentReferenceType) == true || this.currentReferenceType === Enums.ReferenceType.IdDataType) {
                    this.gridService.getPopupColumnModelByReferenceType(this.currentReferenceType)
                        .then((columns: ColumnModel[]) => {
                            this.getGridDataSource(_.map(columns, (column: ColumnModel) => new ColumnInfo(column)), 1, 1, '', value)
                                .then((result: DataProviderListResult<IReferenceType>) => {
                                    this.currentReferenceObject = result.objects
                                    this.dropDownBox.option({ dataSoure: this.currentReferenceObject })
                                })
                        })
                }
            })
    }

    private getReferenceType(id: Enums.ReferenceType): Enums.ReferenceType {
        switch (id) {
            case Enums.ReferenceType.OperatorList:
                return Enums.ReferenceType.IdOperator
            case Enums.ReferenceType.TaskStatusList:
                return Enums.ReferenceType.IdTaskStatus
            case Enums.ReferenceType.LocationList:
                return Enums.ReferenceType.IdLocation
            case Enums.ReferenceType.DistributorList:
                return Enums.ReferenceType.IdDistributor
            case Enums.ReferenceType.MeterTypeList:
                return Enums.ReferenceType.IdMeterType
            case Enums.ReferenceType.DeviceTypeList:
                return Enums.ReferenceType.IdDeviceType
            default:
                return id
        }
    }

    //#endregion

}